/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { delayedUploadImageActions, deleteImage, deleteStaleOfflineImageActions, getFormValue, replaceFormValue, setFormValue, storeLocalImageActions, uploadImageActions } from "..";
import { rescopeDataPath } from '../../react/components/form';
import { ActionsObservable, combineEpics, StateObservable } from "redux-observable";
import { empty, from, merge, of, throwError, timer } from "rxjs";
import { catchError, concatMap, exhaustMap, expand, filter, map, switchMap } from "rxjs/operators";
import { isActionOf } from "typesafe-actions";
import { AppAction, scanImagesToUpload } from "..";
import config from "../../config";
import { getDB, storeNames } from "../../db/store";
import { createEpic } from "../../utils/epics";
import { AppState } from "./..";
import { loadToken } from "./../../utils/token";

const { IMAGE_STORE, OFFLINE_IMAGE_UPDATE_STORE } = storeNames;

const BACKGROUND_SYNC_INTERVAL = 60000; //600000; // 10 minutes

const uploadImageEpic = createEpic((action$: ActionsObservable<AppAction>, state$: StateObservable<AppState>) => {
  return action$.pipe(
    filter(isActionOf(uploadImageActions.request)),
    concatMap(action => {
      const token = loadToken();
      if (token === null) {
        return of(uploadImageActions.failure(new Error("Token not found!")));
      }
      const { path, scope, evalID, id, uri } = action.payload;
      const evalData = state$.value.evals.items.getIn([evalID, "data"]);

      const byteString = atob(uri.split(",")[1]);
      const bytes = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) {
        bytes[i] = byteString.charCodeAt(i);
      }
      let mimeType = uri
        .split(",")[0]
        .split(":")[1]
        .split(";")[0];

      // provide a generic default
      if (!mimeType) {
        mimeType = 'application/octet-stream';
      }

      const blob = new Blob([bytes], { type: mimeType });

      const formData = new FormData();
      formData.append("mime_type", mimeType);
      formData.append("attachment", blob);

      return from(
        fetch(`${config.dfdmHost}/api/attachments`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token!.access_token}`,
          },
          body: formData,
        })
      ).pipe(
        switchMap(response => from(response.json())),
        switchMap(json =>
          from(
            getDB().then(db => {
              if (json.status && json.status !== 200) {
                return throwError("Error pushing image to server").toPromise();
              }
              return db.add(IMAGE_STORE, { id: json.id, uri });
            })
          ).pipe(
            switchMap(() => {
              let newPath: string;
              if (path.endsWith("/-}")) {
                // image was written to an array field
                const imageField = path.replace("/-", "");
                const index = getFormValue(imageField, evalData, scope).findIndex((t: string) => t === id);
                if (index === -1) {
                  /*
                   This SHOULD only happen if the image was removed from the eval after it started uploading, which
                   means it should be ignored. If value is missing and we reach this point too early, the value would
                   be double-added if we make replace include the image. If we omit this replace we may never get to
                   update the id. Brittle dependency to make sure the local ID is present before this ever happens,
                   onus is on the attach-image (delayedimageupload) epic.
                   */
                  return of(uploadImageActions.success({ id, evalID }));
                }

                newPath = imageField.replace("}", "/" + index + "}");
              } else {
                // image was written to a string field
                newPath = path;
              }
              newPath = rescopeDataPath(newPath, scope);

              return from([
                replaceFormValue(newPath, json.id, evalID!),
                uploadImageActions.success({ id, evalID }),
                deleteStaleOfflineImageActions.request({ oldID: id })
              ]);
            })
          )
        ),
        catchError(e => {
          console.log(e);
          return of(uploadImageActions.failure(e));
        })
      );
    })
  );
});

const deleteStaleOfflineImage = createEpic((action$: ActionsObservable<AppAction>, state$: StateObservable<AppState>) => {
  return action$.pipe(
    filter(isActionOf(deleteStaleOfflineImageActions.request)),
    switchMap(action => {
      const { oldID } = action.payload;
      return from(getDB().then(db => db.delete(OFFLINE_IMAGE_UPDATE_STORE, oldID as string))
        .then(() => deleteStaleOfflineImageActions.success()));
    }),
    catchError(e => {
      console.log(e);
      return of(deleteStaleOfflineImageActions.failure(e));
    })
  );
});

const saveImageToLocalDB = createEpic((action$: ActionsObservable<AppAction>, state$: StateObservable<AppState>) => {
  return action$.pipe(
    filter(isActionOf(storeLocalImageActions.request)),
    switchMap(action => {
      const { id, uri } = action.payload;
      return from(getDB().then(db => db.add(IMAGE_STORE, { id, uri })));
    }),
    switchMap(() => from([storeLocalImageActions.success()])),
    catchError(e => {
      console.log(e);
      return of(storeLocalImageActions.failure(e));
    })
  );
});

const delayedUploadImageEpic = createEpic((action$: ActionsObservable<AppAction>, state$: StateObservable<AppState>) => {
  return action$.pipe(
    filter(isActionOf(delayedUploadImageActions.request)),
    concatMap(a => {
      const { path, scope, id, evalID, uri } = a.payload;
      const updatedImageIDs = state$.value.evals.items.getIn([evalID, "data"]);
      const newPath = rescopeDataPath(path, scope);
      /*
       The ordering here is important because if the patch happens after db write, then a race condition could occur
       where the image is detected from a timed check for pending images and uploaded, and its replace action is added
       to the redux queue prior to the originating add patch. So we use this ordering to ensure the add patch exists
       and gets queued prior to the replace patch, which can only occur after db write.
      */
      return merge(
        of(setFormValue(newPath, id, updatedImageIDs, evalID!)),
        from(
          getDB().then(db =>
            db
              .add(OFFLINE_IMAGE_UPDATE_STORE, { path, scope, id, evalID, uri })
              .then(() => delayedUploadImageActions.success({ id, evalID }))
          )
        )
      );
    }),
    catchError(e => {
      console.log(e);
      return of(delayedUploadImageActions.failure(e));
    })
  );
});

const deleteImageEpic = createEpic((action$: ActionsObservable<AppAction>, state$: StateObservable<AppState>) => {
  return action$.pipe(
    filter(isActionOf(deleteImage)),
    concatMap(action => {
      const { path, scope, toRemove, original, evalID } = action.payload;
      const rescopedPath = rescopeDataPath(path, scope);
      const evalData = state$.value.evals.items.getIn([evalID, "data"]);
      return from(
        getDB().then(db =>
          db
            .delete(IMAGE_STORE, toRemove)
            .then(() => db.delete(OFFLINE_IMAGE_UPDATE_STORE, toRemove))
            .then(() =>
              setFormValue(
                rescopedPath,
                original.filter((r: string) => r !== toRemove),
                evalData,
                evalID
              )
            )
        )
      );
    })
  );
});

const delayedUploadTaskEpic = createEpic(action$ => {
  return merge(
    timer(1000, BACKGROUND_SYNC_INTERVAL),
    action$.pipe(filter(isActionOf(scanImagesToUpload), isActionOf(delayedUploadImageActions.success)))
  ).pipe(
    exhaustMap(() =>
      from(
        getDB().then(db =>
          db
            .transaction(OFFLINE_IMAGE_UPDATE_STORE, "readonly")
            .objectStore(OFFLINE_IMAGE_UPDATE_STORE)
            .openCursor()
        )
      ).pipe(
        expand(cursor => (cursor === null ? empty() : from(cursor.continue()))),
        filter(cursor => cursor !== null && cursor.value !== null),
        map(cursor =>
          uploadImageActions.request({
            path: cursor!.value.path,
            scope: cursor!.value.scope,
            evalID: cursor!.value.evalID,
            id: cursor!.value.id,
            uri: cursor!.value.uri,
          })
        )
      )
    )
  );
});

export default combineEpics(
  deleteImageEpic,
  uploadImageEpic,
  delayedUploadTaskEpic,
  delayedUploadImageEpic,
  saveImageToLocalDB,
  deleteStaleOfflineImage
);
